import {
  Directive,
  ElementRef,
  Input,
  NgModule,
  Renderer2,
} from '@angular/core';
import { CommonUtils, StringUtils } from '../../../common/public-api';

/**
 * Watermark directivd
 *
 * @export
 * @class WatermarkDirective
 */
@Directive({
  selector: '[sigvWatermark]',
})
export class WatermarkDirective {
  /**
   * Watermark default options.
   */
  readonly defaultOptions: WatermarkOptions = {
    text: 'Watermark',
    width: 300,
    height: 100,
    fontFamily: 'Arial',
    fontSize: '18px',
    fontWeight: 'normal',
    color: '#999999',
    alpha: 0.5,
    degree: -45,
    lineHeight: 24,
    textAlign: 'center',
    textBaseline: 'middle',
    backgroundRepeat: 'repeat',
    backgroundPosition: '0% 0%',
  };

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  /**
   * Watermark configurations.
   */
  @Input('[sigvWatermark]') options: WatermarkOptions = {};

  //#region Angular lifecycle hook
  ngOnChanges(): void {
    this.generateWatermark(this.options);
  }

  ngOnInit(): void {
    this.generateWatermark(this.options);
  }
  //#endregion

  generateWatermark(options: WatermarkOptions): void {
    const newOptions: WatermarkOptions = Object.assign(
      {},
      this.defaultOptions,
      options
    );
    this.processStyleFromOptions(newOptions);
  }

  private processStyleFromOptions(options: WatermarkOptions): void {
    this.setHTMLElementStyle([
      'background-image',
      `url(${this.createDataUrl(options)})`,
    ]);
    this.setHTMLElementStyle([
      'background-repeat',
      options.backgroundRepeat ?? '',
    ]);
    this.setHTMLElementStyle([
      'background-position',
      options.backgroundPosition ?? '',
    ]);
  }

  private createDataUrl(options: WatermarkOptions): string {
    const width = options.width ?? 0;
    const height = options.height ?? 0;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 0;

    ctx.fillRect(-0.5 * width, -0.5 * height, width, height);

    ctx.font = `${options.fontWeight} ${options.fontSize} ${options.fontFamily}`;
    if (StringUtils.isEmpty(options.color) === false) {
      ctx.fillStyle = options.color as string;
    }
    if (CommonUtils.isNullOrUndefined(options.alpha) === false) {
      ctx.globalAlpha = options.alpha as number;
    }
    if (StringUtils.isEmpty(options.textAlign) === false) {
      ctx.textAlign = options.textAlign as any;
    }
    if (StringUtils.isEmpty(options.textBaseline) === false) {
      ctx.textBaseline = options.textBaseline as any;
    }
    ctx.translate(0 + 0.5 * width, 0 + 0.5 * height);
    if (CommonUtils.isNullOrUndefined(options.degree) === false) {
      ctx.rotate(((options.degree as number) * Math.PI) / 180);
    }
    const text = options.text ?? '';
    const lineHeight = options.lineHeight ?? 0;
    this.generateMultiLineText(ctx, text, width, lineHeight, 0, 0);

    return canvas.toDataURL();
  }

  private generateMultiLineText(
    context: CanvasRenderingContext2D,
    text: string,
    width: number,
    lineHeight: number,
    x: number,
    y: number
  ): void {
    const words = text.split('\n');
    for (const [i, line] of words.entries()) {
      context.fillText(line, x, y);
      y += lineHeight;
    }
  }

  private setHTMLElementStyle([name, value]: [string, string | number]): void {
    this.renderer.setStyle(this.el.nativeElement, name, value);
  }
}

export interface WatermarkOptions {
  text?: string;
  width?: number;
  height?: number;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?:
    | 'normal'
    | 'bold'
    | 'bolder'
    | 'lighter'
    | 100
    | 200
    | 300
    | 400
    | 500
    | 600
    | 700
    | 800
    | 900;
  color?: string;
  alpha?: number;
  degree?: number;
  lineHeight?: number;
  textAlign?: 'start' | 'end' | 'center' | 'left' | 'right';
  textBaseline?:
    | 'alphabetic'
    | 'top'
    | 'hanging'
    | 'middle'
    | 'ideographic'
    | 'bottom';
  backgroundRepeat?:
    | 'repeat'
    | 'repeat-x'
    | 'repeat-y'
    | 'no-repeat'
    | 'space'
    | 'round'
    | 'initial';
  backgroundPosition?: string;
}

@NgModule({
  declarations: [WatermarkDirective],
  imports: [],
  exports: [WatermarkDirective],
})
export class WaterMarkModule {
  constructor() {}
}
